import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getDeviceId } from "../utils/deviceId";

const API = process.env.REACT_APP_API_URL;

// ✅ mismas reglas que ReiniciarPasswordModal
function validatePassword(password) {
  if (!password) return false;
  const minLength = password.length >= 6;
  const hasNumber = /\d/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  return minLength && hasNumber && hasUppercase && hasSymbol;
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [touched, setTouched] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const passwordInvalid = useMemo(() => !validatePassword(password), [password]);

  const passwordMismatch = useMemo(() => {
    return password.length > 0 && confirm.length > 0 && password !== confirm;
  }, [password, confirm]);

  const canSubmit = useMemo(() => {
    if (!token) return false;
    if (loading) return false;
    if (!password || !confirm) return false;
    if (passwordMismatch) return false;
    if (passwordInvalid) return false;
    if (success) return false;
    return true;
  }, [token, loading, password, confirm, passwordMismatch, passwordInvalid, success]);

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit) return;

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "y-device-id": getDeviceId(),
        },
        body: JSON.stringify({
          token,
          password,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        setError(data.error || "Could not reset password.");
        return;
      }

        setSuccess(data.message || "Password updated successfully.");
    } catch (e) {
      console.error(e);
      setError("Server error, please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="w-screen h-screen bg-gray-200 flex justify-center items-center">
      <div className="w-[90%] sm:w-[400px] md:w-[450px] lg:w-[550px] xl:w-[580px] bg-white rounded-2xl shadow-lg p-20 relative">
        <h2 className="text-2xl font-semibold text-gray-900 text-center mb-2">
        Reset Password
      </h2>

      <p className="text-sm text-gray-600 text-center mb-8">
        Create a new password for your account.
      </p>

      {!token && (
        <div className="text-red-500 text-sm mb-4 text-center">
          Missing reset token. Check the email link.
        </div>
      )}

      {error && (
        <div className="text-red-500 text-sm mb-4 text-center">{error}</div>
      )}

      {success && (
        <div className="text-green-600 text-sm mb-4 text-center">
          {success}
        </div>
      )}

        {/* Nueva contraseña */}
        <div className="relative w-full mt-4">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched(true)}
            onKeyDown={onKeyDown}
            required
            placeholder="New password"
            className={`peer block w-full border-b text-gray-900 placeholder-transparent focus:outline-none py-2 ${
              touched && passwordInvalid ? "border-red-500" : "border-gray-300"
            }`}
            autoComplete="new-password"
            disabled={!token || loading || !!success}
          />
          <label
            className="
              absolute left-0 text-gray-400 text-base
              top-2 transition-all duration-200
              peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
              peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
              peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
              pointer-events-none
            "
          >
            New password
          </label>
        </div>

        {/* ✅ Requisitos (igual que el modal) */}
        {touched && passwordInvalid && (
          <div className="bg-green-50 p-4 rounded-md mt-4 mb-2 text-sm text-gray-800">
            Password must have at least{" "}
            <span className="font-semibold">6 characters</span>, contain{" "}
            <span className="font-semibold">1 number</span>,{" "}
            <span className="font-semibold">1 uppercase letter</span> and{" "}
            <span className="font-semibold">1 symbol</span>.
          </div>
        )}

        {/* Confirmar contraseña */}
        <div className="relative w-full mt-6">
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onBlur={() => setTouched(true)}
            onKeyDown={onKeyDown}
            required
            placeholder="Confirm password"
            className={`peer block w-full border-b text-gray-900 placeholder-transparent focus:outline-none py-2 ${
              passwordMismatch ? "border-red-500" : "border-gray-300"
            }`}
            autoComplete="new-password"
            disabled={!token || loading || !!success}
          />
          <label
            className="
              absolute left-0 text-gray-400 text-base
              top-2 transition-all duration-200
              peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
              peer-focus:-top-4 peer-focus:text-sm peer-focus:text-green-600
              peer-valid:-top-4 peer-valid:text-sm peer-valid:text-green-600
              pointer-events-none
            "
          >
            Confirm password
          </label>
        </div>

        {passwordMismatch && (
          <div className="text-red-500 text-xs mt-3 text-center">
            Passwords do not match.
          </div>
        )}

        {/* Botón */}
        {!success ? (
          <button
            onClick={handleSubmit}
            className={`w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-lg mb-6 mt-8 ${
              !canSubmit ? "opacity-70 cursor-not-allowed" : ""
            }`}
            disabled={!canSubmit}
          >
            {loading ? "Saving..." : "Save new password"}
          </button>
        ) : (
          <button
            onClick={() => navigate("/")}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-lg mb-6 mt-8"
          >
            Sign In
          </button>
        )}

        {/* Volver */}
        {!success && (
          <div className="text-center text-sm text-gray-900">
            <span>Already have access?</span>
            <button
              onClick={() => navigate("/")}
              className="text-green-500 font-semibold ml-1"
              type="button"
            >
              Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
